import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { paths } from '@/app/paths';
import { Button, DataTable, Pill, type Column } from '@/design-system';
import { now } from '@/lib/clock';
import { formatRelative } from '@/lib/format';
import { useBreadcrumbs } from '@/shell/breadcrumbs';
import { useGlobalActions } from '@/shell/globalActions';
import { LoadError } from '@/shell/LoadError';
import { PageHeader } from '@/shell/PageHeader';
import { websitesQuery, type Website } from './api';
import { STATUS_TONE } from './status';
import { useVariantLabel } from './useVariantLabel';
import s from './sites.module.css';

/** «landing-only» counts the landings of organizations that have no e-commerce site. */
function counts(sites: Website[]) {
  const ecomOrgs = new Set(sites.filter((x) => x.type === 'ecommerce').map((x) => x.organization.id));
  return {
    production: sites.filter((x) => x.status === 'production').length,
    landing: sites.filter((x) => x.type === 'landing' && !ecomOrgs.has(x.organization.id)).length,
  };
}

export function SitesPage() {
  const { t } = useTranslation('sites');
  const { t: tc } = useTranslation('common');
  useBreadcrumbs([{ label: t('title') }]);
  const navigate = useNavigate();
  const { openCreateSite } = useGlobalActions();
  const q = useQuery(websitesQuery());
  const variant = useVariantLabel();
  const sites = q.data ?? [];
  const c = counts(sites);

  const columns: Column<Website>[] = [
    {
      key: 'site',
      header: t('table.site'),
      width: 'minmax(0,2fr)',
      render: (x) => (
        <div className={s.siteCell}>
          <span className={s.siteName}>{x.name}</span>
          <span className={s.siteOrg}>{x.organization.brand_name}</span>
        </div>
      ),
    },
    { key: 'type', header: t('table.type'), width: 'minmax(0,.8fr)', render: (x) => <span className={s.muted}>{t(`type.${x.type}`)}</span> },
    { key: 'domain', header: t('table.domain'), width: 'minmax(0,1.6fr)', render: (x) => <span className={s.domain}>{x.domain ?? tc('dash')}</span> },
    { key: 'variant', header: t('table.variant'), width: 'minmax(0,.9fr)', render: (x) => <span className={s.muted}>{variant(x)}</span> },
    {
      key: 'status',
      header: t('table.status'),
      width: 'minmax(0,1.1fr)',
      render: (x) => (
        <Pill tone={STATUS_TONE[x.status]} start>
          {t(`status.${x.status}`)}
        </Pill>
      ),
    },
    { key: 'changed', header: t('table.changed'), width: 'minmax(0,.7fr)', render: (x) => <span className={s.changed}>{formatRelative(x.updated_at, now())}</span> },
  ];

  return (
    <>
      <PageHeader
        eyebrow={q.data ? t('eyebrow', { total: t('count', { count: sites.length }), production: c.production, landing: c.landing }) : ''}
        title={t('title')}
        action={
          <Button icon="plus" onClick={() => openCreateSite()}>
            {t('create')}
          </Button>
        }
      />
      {q.isError ? (
        <LoadError error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        q.data && (
          <DataTable
            label={t('table.label')}
            rows={sites}
            rowKey={(x) => x.id}
            columns={columns}
            onRowClick={(x) => navigate(paths.site(x.id))}
            empty={t('table.empty')}
          />
        )
      )}
    </>
  );
}
